import { Fragment, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { EM_DASH } from '~/common/constants/unicode';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';

import { useAsyncFieldValues } from '../../hooks/useAsyncFieldValues';
import { FieldValue } from '../../types';
import { BaseFieldProps } from './fieldTypes';

const COUNTERPARTIES_FIELD_NAME = 'Counterparties';
const COUNTERPARTIES_PREVIEW_LIMIT = 2;

interface SelectValue {
  value: string | number;
  label: string;
}

interface MultipleArrayFieldProps extends BaseFieldProps {
  showAllParties: boolean;
  onShowAllParties: () => void;
}

export const MultipleArrayField = ({
  field,
  editMode,
  editData,
  onEditDataChange,
  isWssField,
  showAllParties,
  onShowAllParties,
}: MultipleArrayFieldProps) => {
  const intl = useIntl();
  const presentValue = editData[field.id] || [];

  const {
    asyncOptions,
    handleSearch,
    handleChange,
    handleAddOption,
    shouldLoadOnMount,
    isPartiesField,
  } = useAsyncFieldValues({
    fieldId: field.id,
    className: field.class_name,
    allowNewOptions: field.allow_new_options,
    currentValue: presentValue,
    onChange: onEditDataChange,
  });

  useEffect(() => {
    if (editMode && shouldLoadOnMount) {
      handleSearch('');
    }
  }, [editMode, shouldLoadOnMount, handleSearch]);

  if (editMode) {
    const currentValue = Array.isArray(presentValue)
      ? presentValue.map((val: SelectValue | string | number) =>
          typeof val === 'object' && val !== null ? val.value : val,
        )
      : [];

    return (
      <Box
        flex="1"
        style={{ display: 'block' }}
        data-testid={`doc-overview-panel-${field.name.replace(/\s/g, '-')}`}
      >
        <Select
          aria-label={field.name}
          name={field.name}
          value={currentValue}
          options={asyncOptions.data}
          isLoading={asyncOptions.isFetching}
          onSearch={handleSearch}
          selectAllCount={asyncOptions.selectAllCount}
          onChange={handleChange}
          onAddOption={handleAddOption}
          disabled={!field.is_editable || isWssField(field)}
          isMulti
          isClearable
          isSearchable
          placeholder={intl.formatMessage(
            {
              id: 'DocumentOverviewPanel.searchFieldName',
              defaultMessage: 'Search for {fieldName}...',
              description:
                'Placeholder text for search field. Example: fieldName = "Contract Type"',
            },
            { fieldName: field.name },
          )}
          noOptionsMessage={
            isPartiesField
              ? intl.formatMessage({
                  id: 'DocumentOverviewPanel.typeAtLeast3Letters',
                  defaultMessage: 'Type in at least 3 letters...',
                  description:
                    'Message for parties fields requiring minimum input length',
                })
              : intl.formatMessage(
                  {
                    id: 'DocumentOverviewPanel.noFieldNameFound',
                    defaultMessage: 'No {fieldName} found',
                    description:
                      'Message when no search results found. Example: fieldName = "Contract Type"',
                  },
                  { fieldName: field.name },
                )
          }
          enableSelectAll
          width="100%"
        />
      </Box>
    );
  }

  const renderDisplayMode = () => {
    if (!field.value || !Array.isArray(field.value) || !field.value.length) {
      return EM_DASH;
    }

    const shouldShowPartiesPreview =
      !showAllParties &&
      field.name === COUNTERPARTIES_FIELD_NAME &&
      field.value.length > COUNTERPARTIES_PREVIEW_LIMIT;

    if (shouldShowPartiesPreview) {
      return (
        <Layout direction="row" align="center" spacing={1} wrap>
          <span>{field.value[0].display_value}</span>
          <span>•</span>
          <span>{field.value[1].display_value}</span>
          <span>•</span>
          <Button
            variant="action"
            text={intl.formatMessage({
              id: 'DocumentOverviewPanel.showAllParties',
              defaultMessage: 'Show All Parties',
              description: 'Button text to show all parties',
            })}
            onClick={onShowAllParties}
            isInline
            PRIVATE_isPadded={false}
          />
        </Layout>
      );
    }

    return (
      <Layout direction="row" spacing={1} wrap>
        {field.value.map((val: FieldValue, index: number) => (
          <Fragment key={val.value}>
            {index > 0 && <span>•</span>}
            <span>{val.display_value}</span>
          </Fragment>
        ))}
      </Layout>
    );
  };

  return renderDisplayMode();
};
